import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, Plus } from "lucide-react";
import { adminApi, formatApiErrorDetail } from "@/lib/adminApi";
import { Field, inputCls, btnPrimary } from "./ui";
import VinylRecord from "@/components/VinylRecord";

const EMPTY = {
  title: "",
  artist: "",
  note: "",
  week_of: "",
  artwork_url: "",
  listen_url: "",
  provider: "",
  embed_url: "",
  preview_url: "",
  related_program: "",
};

export default function SongPanel() {
  const [songs, setSongs] = useState([]);
  const [form, setForm] = useState({ ...EMPTY });
  const [saving, setSaving] = useState(false);

  const load = () =>
    adminApi.get("/songs").then((r) => setSongs(r.songs)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.title.trim() || !form.artist.trim())
      return toast.error("Title and artist are required.");
    setSaving(true);
    try {
      await adminApi.post("/songs", {
        title: form.title,
        artist: form.artist,
        note: form.note || null,
        week_of: form.week_of || null,
        artwork_url: form.artwork_url || null,
        listen_url: form.listen_url || null,
        provider: form.provider || null,
        embed_url: form.embed_url || null,
        preview_url: form.preview_url || null,
        related_program: form.related_program || null,
      });
      toast.success("New Song of the Week is live.");
      setForm({ ...EMPTY });
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (s) => {
    if (!window.confirm(`Remove "${s.title}" by ${s.artist}?`)) return;
    try {
      await adminApi.del(`/songs/${s.id}`);
      toast.success("Song removed.");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div data-testid="admin-song-panel">
      <p className="text-sm text-latte">
        The newest pick automatically becomes the Song of the Week on the homepage.
      </p>

      <div className="mt-8 rounded-2xl border border-gold/30 bg-cocoa p-6 md:p-10">
        <h3 className="font-display text-2xl font-bold text-cream">New Pick</h3>
        <div className="mt-6 grid gap-6 md:grid-cols-2">
          <Field label="Song Title">
            <input data-testid="song-form-title" className={inputCls} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
          </Field>
          <Field label="Artist">
            <input data-testid="song-form-artist" className={inputCls} value={form.artist} onChange={(e) => setForm({ ...form, artist: e.target.value })} />
          </Field>
          <Field label="Why this record (Shi's note)" className="md:col-span-2">
            <textarea data-testid="song-form-note" rows={3} className={inputCls} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
          </Field>
          <Field label="Week label (optional)">
            <input data-testid="song-form-week" className={inputCls} value={form.week_of} onChange={(e) => setForm({ ...form, week_of: e.target.value })} placeholder="Week of July 20, 2026" />
          </Field>
          <Field label="Album / single artwork URL (optional)">
            <input data-testid="song-form-artwork" className={inputCls} value={form.artwork_url} onChange={(e) => setForm({ ...form, artwork_url: e.target.value })} placeholder="https://…" />
          </Field>
          <Field label="Listen URL — where to hear it (optional)">
            <input data-testid="song-form-listen" className={inputCls} value={form.listen_url} onChange={(e) => setForm({ ...form, listen_url: e.target.value })} placeholder="https://open.spotify.com/track/…" />
          </Field>
          <Field label="Provider (optional)">
            <input data-testid="song-form-provider" className={inputCls} value={form.provider} onChange={(e) => setForm({ ...form, provider: e.target.value })} placeholder="Spotify / Apple Music / YouTube" />
          </Field>
          <Field label="Official embed URL (optional)">
            <input data-testid="song-form-embed" className={inputCls} value={form.embed_url} onChange={(e) => setForm({ ...form, embed_url: e.target.value })} placeholder="https://open.spotify.com/embed/track/…" />
          </Field>
          <Field label="Authorized preview URL — only if provided (optional)">
            <input data-testid="song-form-preview" className={inputCls} value={form.preview_url} onChange={(e) => setForm({ ...form, preview_url: e.target.value })} placeholder="Only a rights-authorized preview audio URL" />
          </Field>
          <Field label="Related Program slug (optional)">
            <input data-testid="song-form-related" className={inputCls} value={form.related_program} onChange={(e) => setForm({ ...form, related_program: e.target.value })} placeholder="program-001-slug" />
          </Field>
        </div>
        <button data-testid="song-form-save-button" onClick={save} disabled={saving} className={`${btnPrimary} mt-8 inline-flex items-center gap-2`}>
          <Plus size={16} /> {saving ? "Cutting the record…" : "Publish Pick"}
        </button>
      </div>

      <div className="mt-8 divide-y divide-corduroy rounded-2xl border border-corduroy">
        {songs.map((s, i) => (
          <div key={s.id} className="flex items-center justify-between gap-4 px-6 py-5">
            <div className="flex items-center gap-4">
              <VinylRecord size={52} spin={false} label={i === 0 ? "NOW" : `${songs.length - i}`} />
              <div>
                <p className="font-display text-lg font-bold text-cream">
                  &ldquo;{s.title}&rdquo; <span className="font-editorial italic text-gold">— {s.artist}</span>
                </p>
                <p className="text-xs text-latte">
                  {i === 0 ? "Currently on the homepage" : s.week_of || "Past pick"}
                </p>
              </div>
            </div>
            <button data-testid={`admin-song-delete-${s.id}`} onClick={() => remove(s)} className="flex h-10 w-10 items-center justify-center rounded-full border border-corduroy text-cream transition-colors hover:border-wine hover:bg-wine" aria-label="Delete song">
              <Trash2 size={15} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
